import { useState, useEffect } from "react";
import { Plus, Eye, ExternalLink, Edit3 } from "lucide-react";
import { api, fmt } from "../../lib/api";
import { toast } from "sonner";

/* ----------------- DROPS ----------------- */
function DropForm({ initial, onSaved, onCancel }) {
  const [d, setD] = useState(initial || { title: "", body_md: "", media_url: "", foundational: false, scheduled_for: "", insight_preview: "", quick_win: false, alacarte_price_cents: "", tags: [], youtube_url: "", transcript_md: "", related_links: [], community_announcement: "", flow_embed: "", flow_url: "", thumbnail_url: "", expires_at: "", visibility: "sovereign" });
  const save = async (e) => {
    e.preventDefault();
    const payload = { ...d };
    if (payload.alacarte_price_cents === "" || payload.alacarte_price_cents == null) payload.alacarte_price_cents = null;
    else payload.alacarte_price_cents = Number(payload.alacarte_price_cents);
    if (!payload.scheduled_for) payload.scheduled_for = null;
    if (!payload.expires_at) payload.expires_at = null;
    try {
      if (initial?.id) await api.patch(`/admin/drops/${initial.id}`, payload);
      else await api.post("/admin/drops", payload);
      toast.success(initial?.id ? "Updated" : "Created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const addLink = () => setD({ ...d, related_links: [...(d.related_links || []), { title: "", url: "" }] });
  const updLink = (i, k, v) => { const arr = [...(d.related_links || [])]; arr[i] = { ...arr[i], [k]: v }; setD({ ...d, related_links: arr }); };
  const delLink = (i) => { const arr = [...(d.related_links || [])]; arr.splice(i, 1); setD({ ...d, related_links: arr }); };
  return (
    <form onSubmit={save} className="panel p-6 space-y-4">
      <div><label className="overline">// TITLE</label><input data-testid="drop-title" value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} required /></div>
      <div><label className="overline">// INSIGHT PREVIEW</label><input value={d.insight_preview} onChange={(e) => setD({ ...d, insight_preview: e.target.value })} placeholder="Teaser members see while it's upcoming" /></div>
      <div><label className="overline">// YOUTUBE URL (unlisted is fine — embeds with full controls)</label><input data-testid="drop-youtube" value={d.youtube_url || ""} onChange={(e) => setD({ ...d, youtube_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." /></div>
      <div><label className="overline">// COVER IMAGE URL (used if no YouTube)</label><input value={d.media_url || ""} onChange={(e) => setD({ ...d, media_url: e.target.value })} placeholder="https://..." /></div>
      <div><label className="overline">// BODY (markdown — full body shown on the drop page)</label><textarea rows={10} value={d.body_md} onChange={(e) => setD({ ...d, body_md: e.target.value })} required/></div>
      <div><label className="overline">// TRANSCRIPT (optional — collapsible on drop page)</label><textarea rows={5} value={d.transcript_md || ""} onChange={(e) => setD({ ...d, transcript_md: e.target.value })} placeholder="Paste the YouTube transcript here"/></div>
      <div>
        <label className="overline">// RELATED LINKS</label>
        <div className="space-y-2 mt-2">
          {(d.related_links || []).map((l, i) => (
            <div key={`${l.url || l.title}-${i}`} className="flex gap-2">
              <input value={l.title || ""} onChange={(e) => updLink(i, "title", e.target.value)} placeholder="Title" className="flex-1"/>
              <input value={l.url || ""} onChange={(e) => updLink(i, "url", e.target.value)} placeholder="https://..." className="flex-1"/>
              <button type="button" onClick={() => delLink(i)} className="btn-ghost text-xs !border-ruby !text-ruby">×</button>
            </div>
          ))}
          <button type="button" onClick={addLink} className="btn-ghost text-xs"><Plus className="w-3 h-3"/>Add link</button>
        </div>
      </div>
      <div>
        <label className="overline">// COMMUNITY ANNOUNCEMENT (becomes a pinned gold-banner post when this drop publishes)</label>
        <textarea data-testid="drop-announcement" rows={3} value={d.community_announcement || ""} onChange={(e) => setD({ ...d, community_announcement: e.target.value })} placeholder="A short message that lands in the community feed with a gold banner the moment this drop publishes."/>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="overline">// SCHEDULED FOR</label><input type="datetime-local" value={d.scheduled_for ? d.scheduled_for.slice(0, 16) : ""} onChange={(e) => setD({ ...d, scheduled_for: e.target.value ? new Date(e.target.value).toISOString() : "" })}/></div>
        <div><label className="overline">// A-LA-CARTE CENTS</label><input type="number" value={d.alacarte_price_cents ?? ""} onChange={(e) => setD({ ...d, alacarte_price_cents: e.target.value })} placeholder="2700 = $27"/></div>
      </div>

      <div className="pt-4 border-t border-borderGold">
        <div className="overline mb-1">// EXTERNAL FLOW / EMBED</div>
        <p className="text-textMuted text-xs mb-3">Turn this drop into one of your hosted HTML flows. Choose a link (opens in a new tab) or an iframe (embedded inline with an Expand button). Leave as "Normal drop" for regular content. Because it's a drop, it inherits a-la-carte pricing, scheduling, and expiration.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="overline">// MODE</label>
            <select data-testid="drop-flow-mode" value={d.flow_embed || ""} onChange={(e) => setD({ ...d, flow_embed: e.target.value })}>
              <option value="">Normal drop (no flow)</option>
              <option value="link">Link with description</option>
              <option value="iframe">Embedded iframe (inline + expand)</option>
            </select>
          </div>
          <div><label className="overline">// FLOW URL</label><input data-testid="drop-flow-url" value={d.flow_url || ""} onChange={(e) => setD({ ...d, flow_url: e.target.value })} placeholder="https://your-html-flow.com/..."/></div>
        </div>
        <div className="mt-3"><label className="overline">// THUMBNAIL URL (small card image)</label><input value={d.thumbnail_url || ""} onChange={(e) => setD({ ...d, thumbnail_url: e.target.value })} placeholder="https://... shown on the drop card"/></div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="overline">// VISIBILITY</label>
          <select data-testid="drop-visibility" value={d.visibility || "sovereign"} onChange={(e) => setD({ ...d, visibility: e.target.value })}>
            <option value="public">Public — anyone (even logged out)</option>
            <option value="members">Members — any active member</option>
            <option value="sovereign">Sovereign tier only</option>
            <option value="admin">Admin only (preview/testing)</option>
          </select>
        </div>
        <div>
          <label className="overline">// EXPIRES AT (optional — hidden from members after)</label>
          <input data-testid="drop-expires" type="datetime-local" value={d.expires_at ? d.expires_at.slice(0, 16) : ""} onChange={(e) => setD({ ...d, expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}/>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 items-center">
        <label className="flex items-center gap-2 text-sm text-textMuted"><input type="checkbox" checked={d.foundational} onChange={(e) => setD({ ...d, foundational: e.target.checked })} style={{width:16,height:16}}/> Foundational</label>
        <label className="flex items-center gap-2 text-sm text-textMuted"><input type="checkbox" checked={d.quick_win} onChange={(e) => setD({ ...d, quick_win: e.target.checked })} style={{width:16,height:16}}/> Quick Win</label>
      </div>
      <div className="flex gap-3 pt-3">
        <button type="submit" className="btn-gold">{initial?.id ? "Update drop" : "Create drop"}</button>
        {onCancel && <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>}
      </div>
    </form>
  );
}

export function Drops() {
  const [drops, setDrops] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = () => api.get("/admin/drops").then((r) => setDrops(r.data.drops));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete drop?")) return; await api.delete(`/admin/drops/${id}`); load(); };
  const publishNow = async (id) => { await api.patch(`/admin/drops/${id}`, { published: true, scheduled_for: null }); load(); };
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="overline">// {drops.length} drops total</div>
        {!creating && !editing && <button data-testid="new-drop" onClick={() => setCreating(true)} className="btn-gold"><Plus className="w-4 h-4"/> New drop</button>}
      </div>
      {creating && <div className="mb-6"><DropForm onSaved={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} /></div>}
      {editing && <div className="mb-6"><DropForm initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} /></div>}
      <div className="space-y-3">
        {drops.map((d) => (
          <div key={d.id} className="panel p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="overline mb-1">{d.published ? "PUBLISHED" : d.scheduled_for ? "SCHEDULED" : "DRAFT"} {d.foundational && "// FOUNDATIONAL"} {d.quick_win && "// QUICK WIN"} {d.alacarte_price_cents && `// A-LA-CARTE ${fmt.money(d.alacarte_price_cents)}`}</div>
                <h3 className="font-display text-2xl text-cream">{d.title}</h3>
                <div className="text-xs font-mono text-textDim mt-1">{d.published_at ? `Pub: ${fmt.datetime(d.published_at)}` : d.scheduled_for ? `Sched: ${fmt.datetime(d.scheduled_for)}` : ""}</div>
              </div>
              <div className="flex gap-2">
                {!d.published && <button onClick={() => publishNow(d.id)} className="btn-ghost text-xs !py-2 !px-3">Publish now</button>}
                <button onClick={() => setEditing(d)} className="btn-ghost text-xs !py-2 !px-3">Edit</button>
                <button onClick={() => del(d.id)} className="btn-ghost text-xs !py-2 !px-3 !border-ruby !text-ruby">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ----------------- ARTICLES ----------------- */
function ArticleForm({ initial, onSaved, onCancel }) {
  const [a, setA] = useState(initial || {
    title: "", slug: "", subtitle: "", excerpt: "", body_md: "", cover_image_url: "",
    tags: [], seo_title: "", seo_description: "", og_image_url: "", vault: false,
    scheduled_for: "", sales_copy_md: "", optin_headline: "", optin_cta: "",
    post_kind: "essay", youtube_url: "",
  });
  const [tagInput, setTagInput] = useState((a.tags || []).join(", "));
  const save = async (e) => {
    e.preventDefault();
    const payload = { ...a, tags: tagInput.split(",").map((s) => s.trim()).filter(Boolean) };
    if (!payload.scheduled_for) payload.scheduled_for = null;
    try {
      if (initial?.id) await api.patch(`/admin/articles/${initial.id}`, payload);
      else await api.post("/admin/articles", payload);
      toast.success(initial?.id ? "Updated" : "Created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  return (
    <form onSubmit={save} className="panel p-6 space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div><label className="overline">// TITLE</label><input data-testid="article-title" value={a.title} onChange={(e) => setA({ ...a, title: e.target.value })} required/></div>
        <div><label className="overline">// SLUG (auto if blank)</label><input value={a.slug} onChange={(e) => setA({ ...a, slug: e.target.value })} placeholder="auto-from-title"/></div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="overline">// POST TYPE</label>
          <select data-testid="article-kind" value={a.post_kind || "essay"} onChange={(e) => setA({ ...a, post_kind: e.target.value })}>
            <option value="essay">Essay (full markdown article)</option>
            <option value="short">Short post (1–2 hard-hitting sentences, large styled)</option>
            <option value="youtube">YouTube video (embedded)</option>
          </select>
        </div>
        {a.post_kind === "youtube" && (
          <div><label className="overline">// YOUTUBE URL</label><input data-testid="article-youtube" value={a.youtube_url || ""} onChange={(e) => setA({ ...a, youtube_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=..."/></div>
        )}
      </div>
      <div><label className="overline">// SUBTITLE</label><input value={a.subtitle} onChange={(e) => setA({ ...a, subtitle: e.target.value })}/></div>
      <div><label className="overline">// EXCERPT (1-2 sentences shown on cards/SEO)</label><textarea rows={2} value={a.excerpt} onChange={(e) => setA({ ...a, excerpt: e.target.value })}/></div>
      <div><label className="overline">// COVER IMAGE URL</label><input value={a.cover_image_url} onChange={(e) => setA({ ...a, cover_image_url: e.target.value })} placeholder="https://..."/></div>
      <div><label className="overline">// BODY (markdown)</label><textarea data-testid="article-body" rows={14} value={a.body_md} onChange={(e) => setA({ ...a, body_md: e.target.value })} required/></div>
      <div><label className="overline">// TAGS (comma-separated)</label><input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="money, kairos, dominion"/></div>
      <div className="grid md:grid-cols-2 gap-4">
        <div><label className="overline">// SEO TITLE</label><input value={a.seo_title} onChange={(e) => setA({ ...a, seo_title: e.target.value })} placeholder="Defaults to title"/></div>
        <div><label className="overline">// OG IMAGE URL</label><input value={a.og_image_url} onChange={(e) => setA({ ...a, og_image_url: e.target.value })} placeholder="Defaults to cover"/></div>
      </div>
      <div><label className="overline">// SEO DESCRIPTION</label><textarea rows={2} value={a.seo_description} onChange={(e) => setA({ ...a, seo_description: e.target.value })}/></div>
      <div className="grid md:grid-cols-2 gap-4">
        <div><label className="overline">// SCHEDULED FOR</label><input type="datetime-local" value={a.scheduled_for ? a.scheduled_for.slice(0,16) : ""} onChange={(e) => setA({ ...a, scheduled_for: e.target.value ? new Date(e.target.value).toISOString() : "" })}/></div>
        <div className="flex items-end gap-3"><label className="flex items-center gap-2 text-sm text-textMuted"><input data-testid="article-vault" type="checkbox" checked={a.vault} onChange={(e) => setA({ ...a, vault: e.target.checked })} style={{width:16,height:16}}/> Vault (members only)</label></div>
      </div>
      <div className="pt-4 border-t border-borderGold">
        <div className="overline mb-3">// SALES BLOCK (shown at bottom of FREE articles only)</div>
        <div><label className="overline">// CUSTOM SALES COPY</label><textarea rows={4} value={a.sales_copy_md} onChange={(e) => setA({ ...a, sales_copy_md: e.target.value })} placeholder="Custom pitch shown to readers of this article. Speak directly from this article's topic to the offer."/></div>
        <div className="grid md:grid-cols-2 gap-4 mt-3">
          <div><label className="overline">// OPTIN HEADLINE</label><input value={a.optin_headline} onChange={(e) => setA({ ...a, optin_headline: e.target.value })} placeholder="STAY ON THE LIST"/></div>
          <div><label className="overline">// OPTIN CTA</label><input value={a.optin_cta} onChange={(e) => setA({ ...a, optin_cta: e.target.value })} placeholder="Get the Tuesday transmission"/></div>
        </div>
      </div>
      <div className="flex gap-3 pt-3">
        <button data-testid="article-save" type="submit" className="btn-gold">{initial?.id ? "Update article" : "Create article"}</button>
        {onCancel && <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>}
      </div>
    </form>
  );
}

export function Articles() {
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = () => api.get("/admin/articles").then((r) => setArticles(r.data.articles));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete article?")) return; await api.delete(`/admin/articles/${id}`); load(); };
  const publishNow = async (id) => { await api.patch(`/admin/articles/${id}`, { published: true, scheduled_for: null }); load(); };
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="overline">// {articles.length} articles &middot; {articles.filter(a => a.vault).length} vault</div>
        {!creating && !editing && <button data-testid="new-article" onClick={() => setCreating(true)} className="btn-gold"><Plus className="w-4 h-4"/> New article</button>}
      </div>
      {creating && <div className="mb-6"><ArticleForm onSaved={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} /></div>}
      {editing && <div className="mb-6"><ArticleForm initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} /></div>}
      <div className="space-y-3">
        {articles.map((a) => (
          <div key={a.id} className="panel p-5" data-testid={`admin-article-${a.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="overline mb-1">{a.published ? "PUBLISHED" : a.scheduled_for ? "SCHEDULED" : "DRAFT"} {a.vault && "// VAULT"} {a.tags?.length ? `// ${a.tags.join(" / ")}` : ""}</div>
                <h3 className="font-display text-2xl text-cream truncate">{a.title}</h3>
                <div className="text-xs font-mono text-textDim mt-1 flex flex-wrap gap-3">
                  <span>/{a.slug}</span>
                  {a.published_at && <span>Pub: {fmt.datetime(a.published_at)}</span>}
                  {a.scheduled_for && !a.published && <span>Sched: {fmt.datetime(a.scheduled_for)}</span>}
                  <span className="flex items-center gap-1"><Eye className="w-3 h-3"/>{a.views || 0}</span>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                {!a.published && <button onClick={() => publishNow(a.id)} className="btn-ghost text-xs !py-2 !px-3">Publish now</button>}
                <a href={a.vault ? `/vault/${a.slug}` : `/blog/${a.slug}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs !py-2 !px-3"><ExternalLink className="w-3 h-3"/></a>
                <button onClick={() => setEditing(a)} className="btn-ghost text-xs !py-2 !px-3"><Edit3 className="w-3 h-3"/></button>
                <button onClick={() => del(a.id)} className="btn-ghost text-xs !py-2 !px-3 !border-ruby !text-ruby">Delete</button>
              </div>
            </div>
          </div>
        ))}
        {articles.length === 0 && <p className="text-textMuted">No articles yet. Write the first one.</p>}
      </div>
    </div>
  );
}
